import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import { DataTable } from '@components/tables'
import { ColoredChipCell } from '@components/tables/cells'
import { DialogTitle } from '@components/titles'
import {
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
} from '@mui/material'
import { GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import { ExamRoom as ExamRoomType, RoomType, Site } from '@services/api'
import { Actions } from '@state/actions'
import { useSelector } from '@state/store'
import { findAllRooms, removeExamRoom } from '@state/thunks/roomThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { removeSites } from '../../../state/reducers/siteReducer'
import { ExamRoomForm } from './ExamRoomForm'
import { EditIcon } from 'lucide-react'

export default function ExamRoom() {
  const dispatch = useDispatch()
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [editingExamRoom, setEditingExamRoom] = useState<
    ExamRoomType | undefined
  >(undefined)

  const { loading, examRooms } = useSelector(({ room, loading }) => ({
    examRooms: room.rooms.filter((room) => room.type === RoomType.ExamRoom),
    loading:
      loading[Actions.UPDATE_EXAM_ROOM] ||
      loading[Actions.CREATE_EXAM_ROOM] ||
      loading[Actions.ROOM_FIND_ALL],
  }))

  const columns: GridColumns = [
    { field: 'code', headerName: 'Code', flex: 1 },
    { field: 'label', headerName: 'Label', flex: 1 },
    {
      field: 'color',
      headerName: 'Color',
      flex: 1,
      renderCell: ColoredChipCell,
    },
    {
      field: 'site',
      headerName: 'Site',
      flex: 1,
      valueFormatter: (params) => {
        return (params.value as unknown as Site)?.label
      },
    },
    {
      field: 'waitingRoom',
      headerName: 'Waiting-room',
      flex: 1,
      valueFormatter: (params) => {
        return (params.value as unknown as any)?.label
      },
    },
    {
      field: 'openingTime',
      headerName: 'Opens at',
      flex: 1,
      valueGetter: ({ value }) =>
        `${new Date(value).toLocaleTimeString(navigator.language, {
          hour: '2-digit',
          minute: '2-digit',
        })}`,
    },
    {
      field: 'breakTime',
      headerName: 'Break at',
      flex: 1,
      valueGetter: ({ value }) =>
        `${new Date(value).toLocaleTimeString(navigator.language, {
          hour: '2-digit',
          minute: '2-digit',
        })}`,
    },
    {
      field: 'reopeningTime',
      headerName: 'Re-opens at',
      flex: 1,
      valueGetter: ({ value }) =>
        `${new Date(value).toLocaleTimeString(navigator.language, {
          hour: '2-digit',
          minute: '2-digit',
        })}`,
    },
    {
      field: 'closingTime',
      headerName: 'Closes at',
      flex: 1,
      valueGetter: ({ value }) =>
        `${new Date(value).toLocaleTimeString(navigator.language, {
          hour: '2-digit',
          minute: '2-digit',
        })}`,
    },
    {
      field: 'wheelchairCompatible',
      headerName: 'Wheelchair',
      flex: 1,
      type: 'boolean',
    },
    {
      field: 'stretcherCompatible',
      headerName: 'Stretcher',
      flex: 1,
      type: 'boolean',
    },
    {
      field: 'xrayCompatible',
      headerName: 'X-ray',
      flex: 1,
      type: 'boolean',
    },
    { field: 'shared', headerName: 'Shared', flex: 1, type: 'boolean' },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={() => {
              setEditingExamRoom(params.row)
              setOpenAddDialog(true)
            }}
          >
            <EditIcon />
          </IconButton>
        )
      },
    },
  ]

  const onRefresh = useCallback(() => {
    dispatch(findAllRooms())
    dispatch(findAllSites())
  }, [dispatch])

  useEffect(() => {
    onRefresh()
    return () => {
      dispatch(removeSites())
    }
  }, [dispatch, onRefresh])

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection as number[])
  }
  const _onDeleteConfirm = () => {
    dispatch(removeExamRoom({ id: selectedRows }))
    setIsDeleteDialogOpen(false)
  }

  return (
    <Container maxWidth={false} style={{ height: '93%', padding: '0px 24px' }}>
      <Paper
        elevation={2}
        style={{ width: '100%', borderRadius: '0px 15px 15px 15px' }}
      >
        <DataTable
          rows={examRooms}
          columns={columns}
          loading={loading}
          toolBarProps={{
            title: 'examRooms',
            icon: 'fab fa-uikit',
            onAdd: () => {
              setEditingExamRoom(undefined)
              setOpenAddDialog(true)
            },
            onRefresh,
            onDelete: () => {
              selectedRows.length
                ? setIsDeleteDialogOpen(true)
                : alert('please select at least one room')
            },
          }}
          detailPanelHeight={400}
          checkboxSelection
          onSelectionModelChange={_onSelectionChange}
        />
      </Paper>

      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="lg"
        aria-labelledby="form-dialog-title"
        TransitionProps={{
          onExited: () => setEditingExamRoom(undefined),
        }}
      >
        <DialogTitle
          title={editingExamRoom ? 'editExamRoom' : 'addExamRoom'}
          onClose={() => setOpenAddDialog(false)}
          format
        />
        <DialogContent>
          <ExamRoomForm
            key={editingExamRoom?.id ?? 'new'}
            onClose={() => setOpenAddDialog(false)}
            row={editingExamRoom}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description="Etes vous sur de vouloir supprimer les salles d'examen selectionnées ?"
      />
    </Container>
  )
}
